import { FC, useMemo } from 'react';
import { CaretDownIcon, CopyIcon, MapIcon, PhoneIcon } from 'assets/icons';
import { Customer } from '../../invoices.model';
import { getAddressAsString } from 'containers/jobs/utils';
import { copyToClipBoard } from 'utils/commonUtils';
import { useAppDispatch } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';

interface InvoiceCustomerSectionProps {
  customer: Customer;
  onCustomerClick: () => void;
}

const InvoiceCustomerSection: FC<InvoiceCustomerSectionProps> = ({ customer, onCustomerClick }) => {
  const dispatch = useAppDispatch();

  const addressString = useMemo(() => {
    let address;
    if (customer.billingAddress?.id) {
      address = customer.billingAddress;
    } else if (customer.serviceAddress?.[0]?.id) {
      address = customer.serviceAddress[0];
    }
    return address ? getAddressAsString(address) : undefined;
  }, [customer.billingAddress, customer.serviceAddress]);

  const onCopyAddress = (event: React.MouseEvent) => {
    event.stopPropagation();
    const copied = copyToClipBoard(addressString);
    if (copied) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Address copied to clipboard',
            isMessageHtml: false
          },
          type: NotifierTypes.SUCCESS,
          showClose: true,
          fontStyle: 'text-primary font-normal'
        })
      );
    }
  };

  const onCopyPhoneNumber = (phoneNumber: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const copied = copyToClipBoard(phoneNumber);
    if (copied) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Phone number copied to clipboard',
            isMessageHtml: false
          },
          type: NotifierTypes.SUCCESS,
          showClose: true,
          fontStyle: 'text-primary font-normal'
        })
      );
    }
  };

  return (
    <div id="invoice-customer-info-card-click" className="flex items-center rounded-md bg-white shadow-section">
      <div className="w-full">
        <div
          onClick={onCustomerClick}
          id="invoice-panel-customer-name-click"
          className="flex cursor-pointer items-center justify-between border-b border-secondaryBtn  py-3 pl-5">
          <div id="invoice-panel-customer-name" className="text-17px font-bold text-headingGray">
            {`${customer?.name || ''} ${customer?.lastName || ''}`.trim() || '-'}
          </div>
          <div className="px-3 py-1">
            <CaretDownIcon className="-rotate-90" />
          </div>
        </div>
        <div className="max-w-[95%] space-y-4 px-5 py-4 text-sbase">
          <div className="flex w-full items-center gap-1 text-primaryText">
            <PhoneIcon className="h-[18px] w-[18px] shrink-0" />
            <div className="flex w-full items-center justify-between">
              <div
                id="invoice-panel-customer-phone-number"
                className={`text-sbase ${customer?.phoneNumber ? '' : 'ml-2'}`}>
                {customer?.phoneNumber || '-'}
              </div>
              {customer?.phoneNumber && customer.phoneNumber !== '-' && (
                <div
                  className="ml-2 inline-flex h-[24px] w-[24px] cursor-pointer items-center justify-center
                   rounded-lg hover:bg-gray-200"
                  id="invoice-panel-customer-phone-number-copy-icon"
                  onClick={e => onCopyPhoneNumber(customer.phoneNumber, e)}>
                  <CopyIcon className="h-[18px] w-[18px]" />
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full items-start gap-1 text-primaryText">
            <MapIcon className="mt-0.5 h-[18px] w-[18px] shrink-0" />
            <div className="flex w-full items-start justify-between">
              <div id="invoice-panel-customer-service-address" className={`text-sbase ${addressString ? '' : 'ml-2'}`}>
                {addressString || '-'}
              </div>
              {addressString && (
                <div
                  className="ml-2 inline-flex h-[24px] w-[24px] cursor-pointer items-center justify-center
                   rounded-lg hover:bg-gray-200"
                  id="invoice-panel-customer-service-address-copy-icon"
                  onClick={e => onCopyAddress(e)}>
                  <CopyIcon className="h-[18px] w-[18px]" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCustomerSection;
